'use client';

import type { FragmentType } from 'src/gql';
import { useCallback, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { TableEmptyState } from '@savvy-kit/patterns/table-empty-state';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { unmaskFragment } from 'src/gql';

import type { OnChangeFn, PaginationState } from '@savvy/data-table';
import { DataTable } from '@savvy/data-table';

import type { TaxOverviewTableColumn } from './tax-overview-table-columns';
import type { TaxColumnSelectionParams, TaxColumnSortParams, TaxRow } from './tax-overview-table-list-columns';
import { dedupeItems, reorderColumnsFromIds } from '../../shared/column-ordering';
import { TaxBulkActionInterface } from './bulk-edit/tax-bulk-action-interface';
import { TaxBulkEditDialog } from './bulk-edit/tax-bulk-edit-dialog';
import { useBulkMarkNotInterested } from './bulk-edit/use-bulk-mark-not-interested';
import { useTableRowSelector } from './bulk-edit/use-table-row-selector';
import { EditTaxEnrollmentDialog } from './edit-tax-enrollment-dialog';
import { TaxOverviewPaginationFooterFragment } from './tax-overview-pagination-footer';
import { TaxOverviewTableBodyFragment } from './tax-overview-table-body';
import { TaxOverviewTableBodyRowFragment } from './tax-overview-table-body-row';
import {
  COLUMN_ID_TO_DISPLAY_NAME,
  createTaxColumns,
  DISPLAY_NAME_TO_COLUMN_ID,
} from './tax-overview-table-list-columns';
import { useTaxOverviewParams } from './tax-overview-url-state';

type TaxOverviewTableListProps = {
  readonly queryFrag: FragmentType<typeof TaxOverviewTableBodyFragment> | undefined;
  readonly paginationFrag: FragmentType<typeof TaxOverviewPaginationFooterFragment> | undefined;
  readonly loading: boolean;
  readonly columns: readonly TaxOverviewTableColumn[];
  readonly hasActiveFilters: boolean;
  readonly onClearFilters: () => void;
  readonly onColumnOrderChange: (columns: TaxOverviewTableColumn[]) => void;
};

export function TaxOverviewTableList({
  queryFrag,
  paginationFrag,
  loading,
  columns,
  hasActiveFilters,
  onClearFilters,
  onColumnOrderChange,
}: TaxOverviewTableListProps) {
  const router = useRouter();
  const { enableTaxFiling } = useFlags();
  const taxFilingEnabled = Boolean(enableTaxFiling);
  const { params, setSortBy, goToNextPage, goToPreviousPage, setPageSize } = useTaxOverviewParams();

  const bodyData = unmaskFragment(TaxOverviewTableBodyFragment, queryFrag);
  const paginationData = unmaskFragment(TaxOverviewPaginationFooterFragment, paginationFrag);

  const nodes = useMemo(() => bodyData?.nodes ?? [], [bodyData?.nodes]);
  const totalCount = paginationData?.totalCount ?? 0;
  const pageInfo = paginationData?.pageInfo;

  const rows = useMemo(
    (): TaxRow[] =>
      nodes
        .filter((n): n is NonNullable<typeof n> => n !== null)
        .map(
          (n): TaxRow => ({
            ...unmaskFragment(TaxOverviewTableBodyRowFragment, n),
            clientId: n.clientId,
            taxEnrollmentId: n.taxEnrollmentId ?? null,
          }),
        ),
    [nodes],
  );

  const rowsForSelector = useMemo(
    () =>
      rows.map((r) => ({
        rowId: r.rowId,
        clientId: r.clientId,
        taxEnrollmentId: r.taxEnrollmentId,
        salesforceId: r.salesforceId,
      })),
    [rows],
  );

  const {
    selectedClients,
    selectedCountOnPage,
    allSelected,
    someSelected,
    toggleRow,
    toggleAllOnPage,
    clear: clearSelection,
  } = useTableRowSelector(rowsForSelector);

  const eligibleClients = useMemo(() => selectedClients.filter((c) => c.taxEnrollmentId === null), [selectedClients]);

  const { bulkMarkNotInterested } = useBulkMarkNotInterested();
  const [editingSalesforceId, setEditingSalesforceId] = useState<string | null>(null);
  const [bulkConfirmDialogOpen, setBulkConfirmDialogOpen] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const handleBulkMarkNotInterested = async () => {
    setBulkLoading(true);
    try {
      await bulkMarkNotInterested(eligibleClients);
      clearSelection();
      setBulkConfirmDialogOpen(false);
    } finally {
      setBulkLoading(false);
    }
  };

  const sorts: TaxColumnSortParams = useMemo(
    () => ({
      client: {
        isSorted: params.sortBy === 'client_name',
        direction: params.sortByOrder,
        onSort: () => setSortBy('client_name'),
      },
      filingType: {
        isSorted: params.sortBy === 'filing_type',
        direction: params.sortByOrder,
        onSort: () => setSortBy('filing_type'),
      },
      taxYear: {
        isSorted: params.sortBy === 'tax_year',
        direction: params.sortByOrder,
        onSort: () => setSortBy('tax_year'),
      },
      taxStatus: {
        isSorted: params.sortBy === 'tax_status',
        direction: params.sortByOrder,
        onSort: () => setSortBy('tax_status'),
      },
      taxPrice: {
        isSorted: params.sortBy === 'price',
        direction: params.sortByOrder,
        onSort: () => setSortBy('price'),
      },
    }),
    [params.sortBy, params.sortByOrder, setSortBy],
  );

  const selection: TaxColumnSelectionParams = useMemo(
    () => ({
      selectedClients,
      allSelected,
      someSelected,
      onSelectAll: () => toggleAllOnPage(rowsForSelector),
      onToggleRow: toggleRow,
    }),
    [selectedClients, allSelected, someSelected, toggleAllOnPage, rowsForSelector, toggleRow],
  );

  const tableColumns = useMemo(
    () => createTaxColumns(sorts, columns, selection, setEditingSalesforceId, taxFilingEnabled),
    [sorts, columns, selection, taxFilingEnabled],
  );

  const columnOrder = useMemo(
    () => dedupeItems(['select', ...columns.map((col) => DISPLAY_NAME_TO_COLUMN_ID[col]), 'actions']),
    [columns],
  );

  const handleColumnOrderChange: OnChangeFn<string[]> = useCallback(
    (updaterOrValue) => {
      const nextOrderIds = dedupeItems(
        typeof updaterOrValue === 'function' ? updaterOrValue(columnOrder) : updaterOrValue,
      );
      onColumnOrderChange(
        reorderColumnsFromIds({
          nextOrderIds,
          columnIdToColumn: COLUMN_ID_TO_DISPLAY_NAME,
          visibleColumns: columns,
        }),
      );
    },
    [columnOrder, onColumnOrderChange, columns],
  );

  const paginationState: PaginationState = useMemo(
    () => ({ pageIndex: params.currentPage, pageSize: params.pageSize }),
    [params.currentPage, params.pageSize],
  );

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      const next = typeof updaterOrValue === 'function' ? updaterOrValue(paginationState) : updaterOrValue;
      if (next.pageSize !== params.pageSize) {
        setPageSize(next.pageSize);
      } else if (next.pageIndex > params.currentPage) {
        goToNextPage(pageInfo?.endCursor ?? null);
      } else if (next.pageIndex < params.currentPage) {
        goToPreviousPage(pageInfo?.startCursor ?? null);
      }
    },
    [paginationState, params.pageSize, params.currentPage, setPageSize, goToNextPage, goToPreviousPage, pageInfo],
  );

  const showEmptyFilteredState = rows.length === 0 && !loading && hasActiveFilters;

  const emptyState = showEmptyFilteredState ? (
    <TableEmptyState onButtonClick={onClearFilters} />
  ) : (
    <div className="h-[200px] flex flex-col items-center justify-center">
      <span className="text-[var(--text-secondary)] text-sm">No tax enrollments</span>
    </div>
  );

  return (
    <>
      <DataTable
        columns={tableColumns}
        data={rows}
        features={{
          pagination: {
            manual: true,
            rowCount: totalCount,
            pageSizeOptions: [5, 10, 25, 50],
          },
          columnResizing: true,
          columnOrdering: true,
        }}
        columnOrder={columnOrder}
        onColumnOrderChange={handleColumnOrderChange}
        paginationState={paginationState}
        onPaginationChange={handlePaginationChange}
        classNames={{
          root: 'border-0 rounded-none',
          tableContent: 'border-x-0 border-b-0 border-t border-light rounded-none mx-0',
          headerCell: 'px-4! py-[11px]! [&>div]:pl-0',
          cell: 'px-4! py-[11px]!',
          row: 'cursor-pointer',
        }}
        slotProps={{
          row: ({ row }) => ({
            onClick: (e: React.MouseEvent) => {
              if (!(e.target instanceof Element)) return;
              if (e.target.closest('button, a, [role="button"]') !== null) return;
              const { salesforceId } = row.original;
              if (salesforceId !== null) {
                router.push(`/advisor/dashboard/client/${salesforceId}/tax`);
              }
            },
          }),
        }}
        emptyState={emptyState}
      />
      <TaxBulkActionInterface
        selectedCount={selectedClients.length}
        selectedCountOnPage={selectedCountOnPage}
        pageRowCount={rowsForSelector.length}
        eligibleCount={eligibleClients.length}
        onMarkNotInterested={() => setBulkConfirmDialogOpen(true)}
        onCancel={clearSelection}
      />
      <TaxBulkEditDialog
        open={bulkConfirmDialogOpen}
        onClose={() => setBulkConfirmDialogOpen(false)}
        onConfirm={handleBulkMarkNotInterested}
        eligibleClientCount={eligibleClients.length}
        totalSelectedCount={selectedClients.length}
        loading={bulkLoading}
      />
      <EditTaxEnrollmentDialog
        open={editingSalesforceId !== null}
        onClose={() => setEditingSalesforceId(null)}
        salesforceAccountId={editingSalesforceId}
      />
    </>
  );
}
